import React, { useRef, useState, useEffect } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import Embed from '@editorjs/embed';
import LinkTool from '@editorjs/link';
import { supabase } from '../supabaseClient';

function BlogAdmin() {
  const editorRef = useRef();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [tags, setTags] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [status, setStatus] = useState('draft');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [postId, setPostId] = useState(null);
  const [publishedAt, setPublishedAt] = useState('');
  const [authorId, setAuthorId] = useState('');
  const [users, setUsers] = useState([]);
  const [category, setCategory] = useState('General');
  const [readTime, setReadTime] = useState('');
  const [featured, setFeatured] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [views, setViews] = useState(0);
  const categories = ['General', 'Tech', 'News', 'Science', 'Islamic Studies'];

  // Initialize Editor.js only once
  React.useEffect(() => {
    if (!editorRef.current) {
      editorRef.current = new EditorJS({
        holder: 'editorjs',
        tools: {
          header: Header,
          list: List,
          image: {
            class: ImageTool,
            config: {
              uploader: {
                async uploadByFile(file) {
                  // Upload image to Supabase Storage
                  const filePath = `editor-images/${Date.now()}-${file.name}`;
                  let { data, error } = await supabase.storage.from('blog-files').upload(filePath, file);
                  if (error) return { success: 0 };
                  const url = supabase.storage.from('blog-files').getPublicUrl(filePath).data.publicUrl;
                  return {
                    success: 1,
                    file: { url }
                  };
                }
              }
            }
          },
          embed: Embed,
          linkTool: LinkTool
        },
        autofocus: true,
        minHeight: 300
      });
    }
    return () => {
      if (editorRef.current && editorRef.current.destroy) editorRef.current.destroy();
    };
  }, []);

  useEffect(() => {
    // Fetch users for author dropdown
    async function fetchUsers() {
      const { data, error } = await supabase.from('users').select('id, full_name, email');
      if (!error) setUsers(data);
    }
    fetchUsers();
  }, []);

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const filePath = `cover-${Date.now()}-${file.name}`;
    let { data, error } = await supabase.storage.from('blog-files').upload(filePath, file);
    if (error) {
      setMessage('Cover image upload failed');
      setUploading(false);
      return;
    }
    const url = supabase.storage.from('blog-files').getPublicUrl(filePath).data.publicUrl;
    setCoverImage(url);
    setUploading(false);
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const filePath = `blog-uploads/attachments/${Date.now()}-${file.name}`;
    let { data, error } = await supabase.storage.from('blog-uploads').upload(filePath, file);
    if (error) {
      setMessage('Attachment upload failed');
      setUploading(false);
      return;
    }
    const url = supabase.storage.from('blog-uploads').getPublicUrl(filePath).data.publicUrl;
    // Save file info to blog_files if postId exists
    if (postId) {
      await supabase.from('blog_files').insert([
        {
          post_id: postId,
          file_url: url,
          file_type: file.type,
          file_name: file.name,
          // uploaded_by: ... (set from auth context)
        }
      ]);
      // Refresh attachments
      fetchAttachments(postId);
    }
    setUploading(false);
  };

  const fetchAttachments = async (id) => {
    const { data, error } = await supabase.from('blog_files').select('*').eq('post_id', id);
    if (!error) setAttachments(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    const editor = editorRef.current;
    if (!editor) return;
    const output = await editor.save();
    // Save blog post to Supabase
    const { data, error } = await supabase.from('blog_posts').insert([
      {
        title,
        slug,
        summary,
        tags: tags.split(',').map(t => t.trim()),
        cover_image_url: coverImage,
        status,
        content: output,
        published_at: publishedAt ? new Date(publishedAt).toISOString() : null,
        author_id: authorId,
        category,
        read_time: readTime ? parseInt(readTime) : null,
        featured,
        seo_title: seoTitle,
        seo_description: seoDescription,
        // views: views (auto, don't set on insert)
      }
    ]).select('id, views');
    if (error) {
      setMessage('Error saving blog post: ' + error.message);
      console.error(error);
    } else {
      setMessage('Blog post saved!');
      setTitle('');
      setSlug('');
      setSummary('');
      setTags('');
      setCoverImage('');
      setPublishedAt('');
      setAuthorId('');
      setCategory('General');
      setReadTime('');
      setFeatured(false);
      setSeoTitle('');
      setSeoDescription('');
      setViews(data && data[0] && data[0].views ? data[0].views : 0);
      editor.clear();
      if (data && data[0] && data[0].id) {
        setPostId(data[0].id);
        fetchAttachments(data[0].id);
      }
    }
  };

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Create Blog Post</h2>
      <div className="row">
        <div className="col-lg-8">
          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <h4 className="mb-3 mt-2 border-bottom pb-2">Basic Info</h4>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Title <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} required placeholder="Enter blog post title" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Slug (URL) <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={slug} onChange={e => setSlug(e.target.value)} required placeholder="e.g. my-awesome-post" />
              </div>
              <div className="col-md-6">
                <label className="form-label">Author <span className="text-danger">*</span></label>
                <select className="form-select" value={authorId} onChange={e => setAuthorId(e.target.value)} required>
                  <option value="">Select Author</option>
                  {users.map(u => (
                    <option value={u.id} key={u.id}>{u.full_name || u.email}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Category</label>
                <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}>
                  {categories.map(cat => (
                    <option value={cat} key={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Published Date</label>
                <input type="date" className="form-control" value={publishedAt} onChange={e => setPublishedAt(e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Read Time (minutes)</label>
                <input type="number" className="form-control" value={readTime} onChange={e => setReadTime(e.target.value)} min="1" placeholder="e.g. 4" />
              </div>
              <div className="col-md-12">
                <label className="form-label">Tags (comma separated)</label>
                <input type="text" className="form-control" value={tags} onChange={e => setTags(e.target.value)} placeholder="science, technology, faith" />
              </div>
              <div className="col-md-12">
                <label className="form-label">Summary</label>
                <textarea className="form-control" value={summary} onChange={e => setSummary(e.target.value)} rows={2} placeholder="Short summary for preview" />
              </div>
              <div className="col-md-8">
                <label className="form-label">Cover Image</label>
                <input type="file" className="form-control" accept="image/*" onChange={handleCoverImageUpload} />
                {coverImage && <img src={coverImage} alt="cover" className="mt-2 rounded shadow-sm" style={{maxWidth: '200px'}} />}
              </div>
              <div className="col-md-6">
                <label className="form-label">Status <span className="text-danger">*</span></label>
                <select className="form-select" value={status} onChange={e => setStatus(e.target.value)} required>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="col-md-4 d-flex align-items-center">
                <div className="form-check mt-4">
                  <input className="form-check-input" type="checkbox" checked={featured} onChange={e => setFeatured(e.target.checked)} id="featuredCheck" />
                  <label className="form-check-label fw-bold" htmlFor="featuredCheck">Featured</label>
                </div>
              </div>
            </div>

            {/* Content */}
            <h4 className="mb-3 mt-4 border-bottom pb-2">Content</h4>
            <div className="mb-3">
              <label className="form-label">Content <span className="text-danger">*</span></label>
              <div id="editorjs" className="border rounded bg-white" style={{minHeight: 300}}></div>
            </div>

            {/* SEO & Meta */}
            <h4 className="mb-3 mt-4 border-bottom pb-2">SEO & Meta</h4>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">SEO Title</label>
                <input type="text" className="form-control" value={seoTitle} onChange={e => setSeoTitle(e.target.value)} placeholder="SEO title for Google" />
              </div>
              <div className="col-md-6">
                <label className="form-label">SEO Meta Description</label>
                <textarea className="form-control" value={seoDescription} onChange={e => setSeoDescription(e.target.value)} rows={2} placeholder="SEO meta description" />
              </div>
            </div>

            {/* Advanced/Read-only */}
            {postId && (
              <div className="row g-3 mt-4">
                <div className="col-md-6">
                  <label className="form-label">Views</label>
                  <input type="number" className="form-control" value={views} readOnly />
                </div>
              </div>
            )}

            {/* Attachments */}
            {postId && (
              <div className="mt-5">
                <h4>Attachments</h4>
                <input type="file" className="form-control mb-2" onChange={handleAttachmentUpload} />
                <ul className="list-group">
                  {attachments.map(att => (
                    <li key={att.id} className="list-group-item d-flex align-items-center justify-content-between">
                      <span>
                        <a href={att.file_url} target="_blank" rel="noopener noreferrer">{att.file_name}</a>
                        <span className="badge bg-secondary ms-2">{att.file_type}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 mb-2">
              <button type="submit" className="btn btn-lg btn-primary px-5 shadow-sm" disabled={uploading}>Save Blog Post</button>
              {message && <div className="mt-3 alert alert-info">{message}</div>}
            </div>
          </form>
        </div>
        {/* Sticky summary card for admins on large screens */}
        <div className="col-lg-4 d-none d-lg-block">
          <div className="position-sticky" style={{top: '90px'}}>
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">
              <h5 className="fw-bold mb-2">Post Summary</h5>
              <div><b>Title:</b> {title || <span className="text-muted">(none)</span>}</div>
              <div><b>Status:</b> {status}</div>
              <div><b>Featured:</b> {featured ? 'Yes' : 'No'}</div>
              <div><b>Category:</b> {category}</div>
              <div><b>Author:</b> {users.find(u => u.id === authorId)?.full_name || ''}</div>
              <div><b>Published:</b> {publishedAt || <span className="text-muted">(not set)</span>}</div>
              <div><b>Read Time:</b> {readTime ? `${readTime} min` : <span className="text-muted">(auto)</span>}</div>
              <div><b>Tags:</b> {tags}</div>
              <div><b>SEO Title:</b> {seoTitle}</div>
              <div><b>SEO Desc:</b> {seoDescription}</div>
              {coverImage && <img src={coverImage} alt="cover" className="mt-3 rounded shadow-sm w-100" style={{maxHeight: '120px', objectFit: 'cover'}} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogAdmin; 